'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import {
  earnAbTestBunja,
  earnSummaryBunja,
  earnTradingBonusesBunja,
  getEarnSummaryOfCurrency,
  getEarnTradingBonus,
} from '@flipster/features/src/shared/earn/state';
import { stack, vstack } from '@flipster/styled-system/patterns';

import { CurrencyHeader } from '../currency-header';
import { ActionButtons } from './components/action-buttons';
import { AprAppliedBalance } from './components/apr-applied-balance';
import { AprBreakdown } from './components/apr-breakdown';
import { AprDisplay } from './components/apr-display';
import { PendingReductions } from './components/pending-reductions';
import { ProcessingMintRequests } from './components/processing-mint-requests';
import { TotalBalance } from './components/total-balance';
import { TradeBonusProgressSection } from './components/trade-bonus-progress-section';
import { ViewAllocationSection } from './components/view-allocation-section';

const ActiveAprCard = ({ currency }: { currency: string }) => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { earnTradingBonusesAtom } = useBunja(earnTradingBonusesBunja);

  const earnSummary = useAtomValue(earnSummaryAtom);
  const earnTradingBonuses = useAtomValue(earnTradingBonusesAtom);

  const { appliedAprPct, hasTradeBonus, pendingReductions, showAppliedBalanceDetail, processingMintRequests } =
    getEarnSummaryOfCurrency(currency, earnSummary);
  const { remainingFirstTradeVolume } = getEarnTradingBonus(currency, earnTradingBonuses);

  // [ab test]
  const { showTradingBonusBoostSectionAtom } = useBunja(earnAbTestBunja);
  const showTradingBonusCard = useAtomValue(showTradingBonusBoostSectionAtom);

  const showAllocationSection = hasTradeBonus && remainingFirstTradeVolume.isZero();
  const showTradeBonusProgressSection = hasTradeBonus && remainingFirstTradeVolume.gt(0);
  const showPendingReductions = pendingReductions.length > 0;
  const showProcessingMintRequests = processingMintRequests.length > 0;

  return (
    <div className={stack({ rounded: 8, p: 16, bg: 'background.default.level-1', gap: 16 })}>
      <CurrencyHeader currency={currency} />
      <div className={vstack({ gap: 10 })}>
        <AprDisplay appliedAprPct={appliedAprPct} />
        <div className={stack({ w: { base: '100%', tablet: 600 }, rounded: 8, overflow: 'hidden' })}>
          {showAllocationSection && <ViewAllocationSection currency={currency} />}
          {!showTradingBonusCard && showTradeBonusProgressSection && <TradeBonusProgressSection currency={currency} />}
        </div>
      </div>
      <div className={stack()}>
        <TotalBalance currency={currency} />
        <AprBreakdown currency={currency} />
        {showAppliedBalanceDetail && <AprAppliedBalance currency={currency} />}
        {showPendingReductions && <PendingReductions currency={currency} />}
        {showProcessingMintRequests && <ProcessingMintRequests requests={processingMintRequests} />}
      </div>
      <ActionButtons currency={currency} />
    </div>
  );
};

export default ActiveAprCard;
